import { useMemo, useState } from "react";
import { trpc } from "@/lib/trpc";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Banknote,
  CheckCircle2,
  Clock,
  CreditCard,
  Mail,
  Package,
  Phone,
  Plus,
  Printer,
  ReceiptText,
  RefreshCw,
  ShoppingBag,
  Trash2,
  Truck,
  User,
  WalletCards,
  XCircle,
} from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

type OrderStatus = "new" | "preparing" | "ready" | "delivered" | "cancelled";
type PaymentMethod =
  | "dinheiro"
  | "pix"
  | "credito"
  | "debito"
  | "voucher"
  | "outro";
type OrderItem = {
  productId: number;
  productName: string;
  quantity: number;
  unitPrice: number;
};
type PaymentSplit = { paymentMethod: PaymentMethod; amount: string };

type StoreOrder = {
  id: number;
  clientName: string;
  clientPhone?: string | null;
  clientEmail?: string | null;
  items: OrderItem[];
  itemsValid?: boolean;
  totalAmount: string | number;
  status: OrderStatus;
  paymentStatus?: "pending" | "paid";
  checkoutSaleId?: number | null;
  notes?: string | null;
  adminNotes?: string | null;
  createdAt: Date | string;
};

const STATUS_LABELS: Record<OrderStatus, string> = {
  new: "Novo",
  preparing: "Em separação",
  ready: "Pronto para retirada",
  delivered: "Entregue",
  cancelled: "Cancelado",
};

const STATUS_COLORS: Record<OrderStatus, string> = {
  new: "bg-blue-100 text-blue-800 border-blue-200",
  preparing: "bg-amber-100 text-amber-800 border-amber-200",
  ready: "bg-emerald-100 text-emerald-800 border-emerald-200",
  delivered: "bg-slate-100 text-slate-700 border-slate-200",
  cancelled: "bg-red-100 text-red-800 border-red-200",
};

const STATUS_ICONS: Record<OrderStatus, React.ReactNode> = {
  new: <ShoppingBag className="h-3.5 w-3.5" />,
  preparing: <Package className="h-3.5 w-3.5" />,
  ready: <CheckCircle2 className="h-3.5 w-3.5" />,
  delivered: <Truck className="h-3.5 w-3.5" />,
  cancelled: <XCircle className="h-3.5 w-3.5" />,
};

const PAYMENT_LABELS: Record<PaymentMethod, string> = {
  dinheiro: "Dinheiro",
  pix: "PIX",
  credito: "Crédito",
  debito: "Débito",
  voucher: "Voucher",
  outro: "Outro",
};

const ORDER_FLOW: Exclude<OrderStatus, "cancelled">[] = [
  "new",
  "preparing",
  "ready",
  "delivered",
];

function nextOrderStatus(status: OrderStatus): Exclude<OrderStatus, "cancelled"> | null {
  const step = ORDER_FLOW.indexOf(status as Exclude<OrderStatus, "cancelled">);
  return step >= 0 && step < ORDER_FLOW.length - 1
    ? ORDER_FLOW[step + 1]!
    : null;
}

function orderItems(order: StoreOrder): OrderItem[] {
  return Array.isArray(order.items) ? order.items : [];
}

function escapeReceiptHtml(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function printStoreOrderReceipt(input: {
  order: StoreOrder;
  splits: PaymentSplit[];
  total: number;
  change: number;
  saleId: number;
}): boolean {
  const win = window.open("", "_blank", "width=400,height=580");
  if (!win) return false;
  const { order, splits, total, change, saleId } = input;
  const money = (value: number) => `R$ ${Number(value).toFixed(2).replace(".", ",")}`;
  const itemsHtml = orderItems(order).map((item) =>
    `<div class="row"><span>${escapeReceiptHtml(`${item.quantity}x ${item.productName}`)}</span><span>${money(item.quantity * item.unitPrice)}</span></div>`
  ).join("");
  const splitsHtml = splits.map((split) =>
    `<div class="row"><span>${escapeReceiptHtml(PAYMENT_LABELS[split.paymentMethod])}</span><span>${money(Number(split.amount))}</span></div>`
  ).join("");
  win.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"/><title>Comprovante do pedido #${order.id}</title><style>@page{size:80mm auto;margin:2mm}*{box-sizing:border-box}html,body{width:76mm;margin:0 auto;padding:0}body{font-family:Arial,Helvetica,sans-serif;font-size:10.5pt;line-height:1.22;color:#000}.center{text-align:center}.line{border-top:1px dashed #000;margin:6px 0}.row{display:flex;justify-content:space-between;gap:8px;margin:3px 0}.row span:last-child{text-align:right}.big{font-size:13pt;font-weight:700}.footer{margin-top:8px;font-size:8.5pt;text-align:center}</style></head><body><div class="center"><h2>VENDA DA LOJA</h2><p>Pedido #${escapeReceiptHtml(order.id)} · Venda #${escapeReceiptHtml(saleId)}</p></div><div class="line"></div><div class="row"><span>Data/Hora:</span><span>${escapeReceiptHtml(format(new Date(), "dd/MM/yyyy HH:mm", { locale: ptBR }))}</span></div><div class="row"><span>Cliente:</span><span>${escapeReceiptHtml(order.clientName)}</span></div>${order.clientPhone ? `<div class="row"><span>Telefone:</span><span>${escapeReceiptHtml(order.clientPhone)}</span></div>` : ""}<div class="line"></div><p><strong>Produtos:</strong></p>${itemsHtml}<div class="line"></div><div class="row big"><span>TOTAL PAGO:</span><span>${money(total)}</span></div><div class="line"></div><p><strong>Formas de pagamento:</strong></p>${splitsHtml}${change > 0 ? `<div class="row"><span>Troco:</span><span>${money(change)}</span></div>` : ""}<div class="line"></div><div class="center footer"><p>Obrigado pela preferência!</p><p>Impresso em ${escapeReceiptHtml(format(new Date(), "dd/MM/yyyy HH:mm", { locale: ptBR }))}</p></div></body></html>`);
  win.document.close();
  win.focus();
  setTimeout(() => { win.print(); win.close(); }, 500);
  return true;
}

export default function AdminStoreOrders() {
  const [filterStatus, setFilterStatus] = useState<OrderStatus | "all">("all");
  const [selectedOrder, setSelectedOrder] = useState<StoreOrder | null>(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [cancellationOrder, setCancellationOrder] = useState<StoreOrder | null>(null);
  const [cancellationReason, setCancellationReason] = useState("");
  const [checkoutOrder, setCheckoutOrder] = useState<StoreOrder | null>(null);
  const [paymentSplits, setPaymentSplits] = useState<PaymentSplit[]>([]);
  const [checkoutNotes, setCheckoutNotes] = useState("");
  const [cashReceived, setCashReceived] = useState("");
  const [checkoutFinalConfirmationOpen, setCheckoutFinalConfirmationOpen] = useState(false);
  const [storeOrderReceipt, setStoreOrderReceipt] = useState<{
    order: StoreOrder;
    splits: PaymentSplit[];
    total: number;
    change: number;
    saleId: number;
  } | null>(null);
  const [storeReceiptPrintAttempted, setStoreReceiptPrintAttempted] = useState(false);

  const utils = trpc.useUtils();
  const { data: allOrders = [], isLoading } = trpc.storeOrders.list.useQuery();
  // allOrders.filter((order) => order.status === filterStatus)
  const orders =
    filterStatus === "all"
      ? allOrders
      : allOrders.filter(order => order.status === filterStatus);
  const newOrdersCount = allOrders.filter(
    order => order.status === "new"
  ).length;

  const updateStatus = trpc.storeOrders.updateStatus.useMutation({
    onSuccess: () => {
      toast.success("Status do pedido atualizado!");
      utils.storeOrders.list.invalidate();
      setSelectedOrder(null);
      setCancellationOrder(null);
      setCancellationReason("");
    },
    onError: err => toast.error(err.message),
  });

  const checkout = trpc.storeOrders.checkout.useMutation({
    onSuccess: result => {
      toast.success(`Pagamento registrado no PDV · Venda #${result.saleId}`);
      utils.storeOrders.list.invalidate();
      utils.products.list.invalidate();
      (utils as any).products.listStandaloneSales.invalidate();
      (utils as any).checkout.report.invalidate();
      if (checkoutOrder) {
        setStoreOrderReceipt({
          order: checkoutOrder,
          splits: paymentSplits.map((split) => ({ ...split })),
          total: Number(checkoutOrder.totalAmount),
          change,
          saleId: result.saleId,
        });
        setStoreReceiptPrintAttempted(false);
      }
      setCheckoutFinalConfirmationOpen(false);
      setCheckoutOrder(null);
      setPaymentSplits([]);
      setCheckoutNotes("");
      setCashReceived("");
    },
    onError: err => toast.error(err.message),
  });

  const checkoutTotal = Number(checkoutOrder?.totalAmount ?? 0);
  const splitTotal = useMemo(
    () =>
      paymentSplits.reduce(
        (sum, split) => sum + (Number(split.amount) || 0),
        0
      ),
    [paymentSplits]
  );
  const remaining = Math.max(0, checkoutTotal - splitTotal);
  const isBalanced = Math.abs(splitTotal - checkoutTotal) < 0.01;
  const hasSingleCashSplit =
    paymentSplits.length === 1 &&
    paymentSplits[0]?.paymentMethod === "dinheiro";
  const change =
    hasSingleCashSplit && Number(cashReceived) > checkoutTotal
      ? Number(cashReceived) - checkoutTotal
      : 0;
  const selectedOrderIsPaid =
    selectedOrder?.paymentStatus === "paid" || !!selectedOrder?.checkoutSaleId;
  const selectedOrderNextStatus = selectedOrder
    ? nextOrderStatus(selectedOrder.status)
    : null;

  const openOrder = (order: StoreOrder) => {
    setSelectedOrder(order);
    setAdminNotes(order.adminNotes ?? "");
  };

  const openCheckout = (order: StoreOrder) => {
    if (order.itemsValid === false || orderItems(order).length === 0) {
      toast.error(
        "Este pedido não possui itens válidos para finalizar. Revise-o antes de registrar o pagamento."
      );
      return;
    }
    if (order.status === "cancelled") {
      toast.error("Não é possível finalizar um pedido cancelado.");
      return;
    }
    if (order.paymentStatus === "paid" || order.checkoutSaleId) {
      toast.error("Este pedido já foi finalizado no PDV.");
      return;
    }
    setSelectedOrder(null);
    setCheckoutOrder(order);
    setPaymentSplits([
      {
        paymentMethod: "dinheiro",
        amount: Number(order.totalAmount).toFixed(2),
      },
    ]);
    setCheckoutNotes("");
    setCashReceived("");
  };

  const handleSaveNotes = () => {
    if (!selectedOrder) return;
    updateStatus.mutate({
      id: selectedOrder.id,
      status: selectedOrder.status,
      adminNotes,
    });
  };

  const handleAdvanceOrder = () => {
    if (!selectedOrder) return;
    const nextStatus = nextOrderStatus(selectedOrder.status);
    if (!nextStatus) return;
    updateStatus.mutate({
      id: selectedOrder.id,
      status: nextStatus,
      adminNotes,
    });
  };

  const handleCancelOrder = () => {
    if (!cancellationOrder) return;
    if (!cancellationReason.trim()) {
      toast.error("Informe o motivo do cancelamento.");
      return;
    }
    updateStatus.mutate({
      id: cancellationOrder.id,
      status: "cancelled",
      cancellationReason: cancellationReason.trim(),
    });
  };

  const updateSplit = (
    index: number,
    field: keyof PaymentSplit,
    value: string
  ) => {
    setPaymentSplits(current =>
      current.map((split, splitIndex) =>
        splitIndex === index ? { ...split, [field]: value } : split
      )
    );
  };

  const addPaymentSplit = () => {
    setPaymentSplits(current => [
      ...current,
      {
        paymentMethod: "pix",
        amount: remaining > 0 ? remaining.toFixed(2) : "",
      },
    ]);
  };

  const openCheckoutFinalConfirmation = () => {
    if (!checkoutOrder) return;
    if (!isBalanced) {
      toast.error(
        `O pagamento precisa somar R$ ${checkoutTotal.toFixed(2).replace(".", ",")}.`
      );
      return;
    }
    setCheckoutFinalConfirmationOpen(true);
  };

  const submitCheckout = () => {
    if (!checkoutOrder || !isBalanced) return;
    checkout.mutate({
      orderId: checkoutOrder.id,
      splits: paymentSplits.map(split => ({
        paymentMethod: split.paymentMethod,
        amount: Number(split.amount),
      })),
      notes: checkoutNotes.trim() || undefined,
    });
  };

  return (
    <div className="space-y-5 p-4 sm:space-y-6 sm:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-bold text-foreground sm:text-2xl">
            <ShoppingBag className="h-5 w-5 text-primary sm:h-6 sm:w-6" />
            Pedidos da Loja
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Confira os itens e finalize o pagamento diretamente no PDV.
          </p>
        </div>
        {newOrdersCount > 0 && (
          <Badge className="w-fit bg-blue-600 px-3 py-1 text-sm text-white">
            {newOrdersCount} novo{newOrdersCount > 1 ? "s" : ""}
          </Badge>
        )}
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
        <Select
          value={filterStatus}
          onValueChange={value => setFilterStatus(value as OrderStatus | "all")}
        >
          <SelectTrigger className="w-full sm:w-52">
            <SelectValue placeholder="Filtrar por status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os pedidos</SelectItem>
            <SelectItem value="new">Novos</SelectItem>
            <SelectItem value="preparing">Em separação</SelectItem>
            <SelectItem value="ready">Prontos</SelectItem>
            <SelectItem value="delivered">Entregues</SelectItem>
            <SelectItem value="cancelled">Cancelados</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          className="w-full sm:w-auto"
          onClick={() => utils.storeOrders.list.invalidate()}
        >
          <RefreshCw className="mr-1.5 h-4 w-4" /> Atualizar
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12 text-muted-foreground">
          <RefreshCw className="mr-2 h-5 w-5 animate-spin" />
          Carregando pedidos...
        </div>
      ) : orders.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <ShoppingBag className="mb-3 h-12 w-12 text-muted-foreground/40" />
            <p className="font-medium text-muted-foreground">
              Nenhum pedido encontrado
            </p>
            <p className="mt-1 text-sm text-muted-foreground/70">
              Os pedidos da loja pública aparecerão aqui.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 sm:gap-4">
          {orders.map(order => {
            const items = orderItems(order);
            const status = order.status;
            const isPaid =
              order.paymentStatus === "paid" || !!order.checkoutSaleId;
            return (
              <Card
                key={order.id}
                className="cursor-pointer border transition-shadow hover:shadow-md"
                onClick={() => openOrder(order)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="mb-2 flex flex-wrap items-center gap-1.5">
                        <span className="font-semibold text-foreground">
                          Pedido #{order.id}
                        </span>
                        <span
                          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[status]}`}
                        >
                          {STATUS_ICONS[status]}
                          {STATUS_LABELS[status]}
                        </span>
                        {isPaid ? (
                          <Badge
                            variant="outline"
                            className="border-emerald-300 bg-emerald-50 text-emerald-800"
                          >
                            Pago no PDV
                          </Badge>
                        ) : (
                          <Badge
                            variant="outline"
                            className="border-amber-300 bg-amber-50 text-amber-800"
                          >
                            Aguardando pagamento
                          </Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <User className="h-3.5 w-3.5" />
                          {order.clientName}
                        </span>
                        {order.clientPhone && (
                          <span className="flex items-center gap-1">
                            <Phone className="h-3.5 w-3.5" />
                            {order.clientPhone}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5" />
                          {format(
                            new Date(order.createdAt),
                            "dd/MM/yyyy HH:mm",
                            { locale: ptBR }
                          )}
                        </span>
                      </div>
                      <p className="mt-2 line-clamp-1 text-xs text-muted-foreground">
                        {items.length > 0
                          ? items
                              .map(
                                item => `${item.quantity}x ${item.productName}`
                              )
                              .join(" · ")
                          : "Itens precisam ser revisados"}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-lg font-bold text-primary">
                        R${" "}
                        {Number(order.totalAmount).toFixed(2).replace(".", ",")}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {items.length} item{items.length !== 1 ? "ns" : ""}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog
        open={!!selectedOrder}
        onOpenChange={open => !open && setSelectedOrder(null)}
      >
        <DialogContent className="max-h-[92dvh] max-w-lg overflow-y-auto p-5 sm:p-6">
          <DialogHeader>
            <DialogTitle>Pedido #{selectedOrder?.id}</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-5">
              <div className="rounded-lg bg-muted/50 p-3 text-sm">
                <p className="flex items-center gap-1.5 font-medium">
                  <User className="h-4 w-4 text-muted-foreground" />
                  {selectedOrder.clientName}
                </p>
                {selectedOrder.clientPhone && (
                  <p className="mt-1 flex items-center gap-1.5 text-muted-foreground">
                    <Phone className="h-3.5 w-3.5" />
                    {selectedOrder.clientPhone}
                  </p>
                )}
                {selectedOrder.clientEmail && (
                  <p className="mt-1 flex items-center gap-1.5 text-muted-foreground">
                    <Mail className="h-3.5 w-3.5" />
                    {selectedOrder.clientEmail}
                  </p>
                )}
              </div>

              <div>
                <p className="mb-2 text-sm font-semibold">Itens do pedido</p>
                {selectedOrder.itemsValid === false ? (
                  <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                    Os itens deste pedido não puderam ser validados. Não
                    finalize antes de revisar.
                  </p>
                ) : (
                  <div className="space-y-2 rounded-lg border p-3">
                    {orderItems(selectedOrder).map(item => (
                      <div
                        key={item.productId}
                        className="flex justify-between gap-3 text-sm"
                      >
                        <span className="min-w-0">
                          <strong>{item.quantity}x</strong> {item.productName}
                        </span>
                        <span className="shrink-0 text-muted-foreground">
                          R${" "}
                          {(item.quantity * item.unitPrice)
                            .toFixed(2)
                            .replace(".", ",")}
                        </span>
                      </div>
                    ))}
                    <div className="flex justify-between border-t pt-2 text-sm font-bold">
                      <span>Total</span>
                      <span className="text-primary">
                        R${" "}
                        {Number(selectedOrder.totalAmount)
                          .toFixed(2)
                          .replace(".", ",")}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {selectedOrder.notes && (
                <div>
                  <p className="mb-1 text-sm font-semibold">
                    Observações do cliente
                  </p>
                  <p className="rounded-lg bg-muted/50 p-2 text-sm text-muted-foreground">
                    {selectedOrder.notes}
                  </p>
                </div>
              )}

              {selectedOrder.paymentStatus === "paid" ||
              selectedOrder.checkoutSaleId ? (
                <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                  <CheckCircle2 className="h-4 w-4" />
                  Pagamento registrado no PDV · Venda #
                  {selectedOrder.checkoutSaleId}
                </div>
              ) : (
                <Button
                  className="h-11 w-full gap-2"
                  onClick={() => openCheckout(selectedOrder)}
                  disabled={
                    selectedOrder.itemsValid === false ||
                    selectedOrder.status === "cancelled"
                  }
                >
                  <ReceiptText className="h-4 w-4" />
                  Finalizar pagamento no PDV
                </Button>
              )}

              <div className="space-y-4 border-t pt-4">
                <div>
                  <p className="text-sm font-semibold">Andamento do pedido</p>
                  {selectedOrder.status === "cancelled" ? (
                    <div className="mt-2 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-800">
                      <XCircle className="h-4 w-4" />
                      Pedido cancelado
                    </div>
                  ) : (
                    <ol className="mt-3 grid grid-cols-4 gap-1" aria-label="Etapas do pedido">
                      {ORDER_FLOW.map((step, index) => {
                        const currentIndex = ORDER_FLOW.indexOf(selectedOrder.status as Exclude<OrderStatus, "cancelled">);
                        const isCurrent = selectedOrder.status === step;
                        const isComplete = currentIndex > index;
                        return (
                          <li key={step} className="min-w-0 text-center">
                            <span
                              className={`mx-auto flex h-7 w-7 items-center justify-center rounded-full border text-xs font-bold ${
                                isCurrent
                                  ? "border-primary bg-primary text-primary-foreground"
                                  : isComplete
                                    ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                                    : "border-muted-foreground/30 bg-muted text-muted-foreground"
                              }`}
                            >
                              {isComplete ? <CheckCircle2 className="h-4 w-4" /> : index + 1}
                            </span>
                            <span className={`mt-1 block text-[10px] leading-tight ${isCurrent ? "font-semibold text-foreground" : "text-muted-foreground"}`}>
                              {STATUS_LABELS[step]}
                            </span>
                          </li>
                        );
                      })}
                    </ol>
                  )}
                </div>

                {selectedOrder.adminNotes && (
                  <div className="rounded-lg bg-muted/50 p-3 text-sm">
                    <p className="mb-1 font-medium">Anotações e histórico</p>
                    <p className="whitespace-pre-wrap text-muted-foreground">
                      {selectedOrder.adminNotes}
                    </p>
                  </div>
                )}

                {selectedOrderNextStatus && (
                  <Button
                    className="h-11 w-full gap-2"
                    onClick={handleAdvanceOrder}
                    disabled={updateStatus.isPending}
                  >
                    {updateStatus.isPending ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      STATUS_ICONS[selectedOrderNextStatus]
                    )}
                    {updateStatus.isPending
                      ? "Atualizando..."
                      : `Avançar para ${STATUS_LABELS[selectedOrderNextStatus]}`}
                  </Button>
                )}

                {selectedOrder.status === "delivered" && (
                  <p className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                    Entrega confirmada. Este pedido não pode voltar para etapas anteriores.
                  </p>
                )}

                {selectedOrder.status !== "delivered" && selectedOrder.status !== "cancelled" && (
                  <div className="space-y-2">
                    {selectedOrderIsPaid && (
                      <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                        Pagamento já registrado no PDV. Para cancelar, é necessário primeiro um fluxo de estorno autorizado.
                      </p>
                    )}
                    <Button
                      variant="outline"
                      className="h-10 w-full border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                      disabled={selectedOrderIsPaid || updateStatus.isPending}
                      onClick={() => {
                        setCancellationReason("");
                        setCancellationOrder(selectedOrder);
                        setSelectedOrder(null);
                      }}
                    >
                      <XCircle className="mr-2 h-4 w-4" />
                      Cancelar pedido
                    </Button>
                  </div>
                )}

                {selectedOrder.status !== "delivered" && selectedOrder.status !== "cancelled" ? (
                  <div className="space-y-2 border-t pt-4">
                    <label className="text-sm font-medium" htmlFor="store-order-admin-notes">
                      Anotações para a equipe
                    </label>
                    <Textarea
                      id="store-order-admin-notes"
                      value={adminNotes}
                      onChange={event => setAdminNotes(event.target.value)}
                      placeholder="Anotações internas da equipe..."
                      rows={2}
                    />
                    <div className="flex gap-2 pt-1">
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={() => setSelectedOrder(null)}
                      >
                        Fechar
                      </Button>
                      <Button
                        className="flex-1"
                        onClick={handleSaveNotes}
                        disabled={updateStatus.isPending}
                      >
                        {updateStatus.isPending ? "Salvando..." : "Salvar anotações"}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => setSelectedOrder(null)}
                  >
                    Fechar
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!cancellationOrder}
        onOpenChange={open =>
          !open && !updateStatus.isPending && setCancellationOrder(null)
        }
      >
        <DialogContent className="max-w-md p-5 sm:p-6">
          {cancellationOrder && (
            <div className="space-y-5">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-red-800">
                  <XCircle className="h-5 w-5" />
                  Cancelar pedido #{cancellationOrder.id}
                </DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground">
                O pedido de <strong className="text-foreground">{cancellationOrder.clientName}</strong> será encerrado. Esta ação não altera pagamentos nem estoque.
              </p>
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="store-order-cancellation-reason">
                  Motivo do cancelamento <span className="text-destructive">*</span>
                </label>
                <Textarea
                  id="store-order-cancellation-reason"
                  value={cancellationReason}
                  onChange={event => setCancellationReason(event.target.value)}
                  placeholder="Ex.: cliente desistiu da compra"
                  rows={3}
                  autoFocus
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  disabled={updateStatus.isPending}
                  onClick={() => setCancellationOrder(null)}
                >
                  Voltar
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1"
                  disabled={updateStatus.isPending || !cancellationReason.trim()}
                  onClick={handleCancelOrder}
                >
                  {updateStatus.isPending ? "Cancelando..." : "Confirmar cancelamento"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!checkoutOrder}
        onOpenChange={open => {
          if (!open && !checkout.isPending) {
            setCheckoutOrder(null);
            setCheckoutFinalConfirmationOpen(false);
          }
        }}
      >
        <DialogContent className="max-h-[94dvh] max-w-lg overflow-y-auto p-5 sm:p-6">
          {checkoutOrder && (
            <div className="space-y-5">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <WalletCards className="h-5 w-5 text-primary" />
                  Checkout do PDV
                </DialogTitle>
              </DialogHeader>
              <div className="rounded-xl border bg-muted/30 p-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="font-semibold">Pedido #{checkoutOrder.id}</p>
                    <p className="text-sm text-muted-foreground">
                      {checkoutOrder.clientName}
                    </p>
                  </div>
                  <p className="text-lg font-bold text-primary">
                    R$ {checkoutTotal.toFixed(2).replace(".", ",")}
                  </p>
                </div>
                <div className="mt-3 space-y-1 border-t pt-2 text-xs text-muted-foreground">
                  {orderItems(checkoutOrder).map(item => (
                    <div className="flex justify-between" key={item.productId}>
                      <span>
                        {item.quantity}x {item.productName}
                      </span>
                      <span>
                        R${" "}
                        {(item.quantity * item.unitPrice)
                          .toFixed(2)
                          .replace(".", ",")}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold">Formas de pagamento</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 gap-1 text-primary"
                    onClick={addPaymentSplit}
                  >
                    <Plus className="h-4 w-4" />
                    Adicionar
                  </Button>
                </div>
                {paymentSplits.map((split, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Select
                      value={split.paymentMethod}
                      onValueChange={value =>
                        updateSplit(index, "paymentMethod", value)
                      }
                    >
                      <SelectTrigger className="min-w-0 flex-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(Object.keys(PAYMENT_LABELS) as PaymentMethod[]).map(
                          method => (
                            <SelectItem key={method} value={method}>
                              {PAYMENT_LABELS[method]}
                            </SelectItem>
                          )
                        )}
                      </SelectContent>
                    </Select>
                    <div className="relative w-28 shrink-0">
                      <span className="pointer-events-none absolute left-3 top-2.5 text-sm text-muted-foreground">
                        R$
                      </span>
                      <Input
                        inputMode="decimal"
                        aria-label={`Valor da forma ${index + 1}`}
                        className="pl-9"
                        value={split.amount}
                        onChange={event =>
                          updateSplit(index, "amount", event.target.value)
                        }
                      />
                    </div>
                    {paymentSplits.length > 1 && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="shrink-0 text-muted-foreground hover:text-destructive"
                        onClick={() =>
                          setPaymentSplits(current =>
                            current.filter(
                              (_, splitIndex) => splitIndex !== index
                            )
                          )
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="sr-only">
                          Remover forma de pagamento
                        </span>
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              {hasSingleCashSplit && (
                <div className="space-y-2">
                  <label
                    className="text-sm font-medium"
                    htmlFor="cash-received"
                  >
                    Valor recebido em dinheiro
                  </label>
                  <div className="relative">
                    <span className="pointer-events-none absolute left-3 top-2.5 text-sm text-muted-foreground">
                      R$
                    </span>
                    <Input
                      id="cash-received"
                      inputMode="decimal"
                      className="pl-9"
                      value={cashReceived}
                      onChange={event => setCashReceived(event.target.value)}
                      placeholder={checkoutTotal.toFixed(2)}
                    />
                  </div>
                  {change > 0 && (
                    <p className="flex items-center gap-1 text-sm text-emerald-700">
                      <Banknote className="h-4 w-4" />
                      Troco: R$ {change.toFixed(2).replace(".", ",")}
                    </p>
                  )}
                </div>
              )}

              <Textarea
                value={checkoutNotes}
                onChange={event => setCheckoutNotes(event.target.value)}
                placeholder="Observação do checkout (opcional)"
                rows={2}
              />
              <div
                className={`flex items-center justify-between rounded-lg p-3 text-sm ${isBalanced ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"}`}
              >
                <span>
                  {isBalanced
                    ? "Pagamento conferido"
                    : `Faltam R$ ${remaining.toFixed(2).replace(".", ",")}`}
                </span>
                {isBalanced ? (
                  <CheckCircle2 className="h-4 w-4" />
                ) : (
                  <CreditCard className="h-4 w-4" />
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  disabled={checkout.isPending}
                  onClick={() => {
                    setCheckoutOrder(null);
                    setCheckoutFinalConfirmationOpen(false);
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  className="flex-1 gap-1"
                  disabled={!isBalanced || checkout.isPending}
                  onClick={openCheckoutFinalConfirmation}
                >
                  {checkout.isPending ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      Finalizando...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4" />
                      Revisar pagamento
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={checkoutFinalConfirmationOpen}
        onOpenChange={open => !open && !checkout.isPending && setCheckoutFinalConfirmationOpen(false)}
      >
        <DialogContent className="max-h-[92dvh] max-w-lg overflow-y-auto p-5 sm:p-6">
          {checkoutOrder && (
            <div className="space-y-5">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <ReceiptText className="h-5 w-5 text-primary" />
                  Conferência final do pedido
                </DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground">
                Confira os dados. O pagamento só será registrado depois da confirmação abaixo.
              </p>
              <div className="space-y-3 rounded-xl border bg-muted/30 p-3 text-sm">
                <div className="flex justify-between gap-3">
                  <span className="text-muted-foreground">Pedido</span>
                  <span className="font-semibold">#{checkoutOrder.id}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-muted-foreground">Cliente</span>
                  <span className="text-right font-semibold">{checkoutOrder.clientName}</span>
                </div>
                <div className="space-y-1.5 border-t pt-3 text-muted-foreground">
                  {orderItems(checkoutOrder).map((item) => (
                    <div className="flex justify-between gap-3" key={item.productId}>
                      <span className="min-w-0">{item.quantity}x {item.productName}</span>
                      <span className="shrink-0">R$ {(item.quantity * item.unitPrice).toFixed(2).replace(".", ",")}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between border-t pt-3 text-base font-bold text-primary">
                  <span>Total</span>
                  <span>R$ {checkoutTotal.toFixed(2).replace(".", ",")}</span>
                </div>
              </div>
              <div className="rounded-xl border p-3 text-sm">
                <p className="mb-2 font-medium">Formas de pagamento</p>
                <div className="space-y-1.5">
                  {paymentSplits.map((split, index) => (
                    <div className="flex justify-between gap-3 text-muted-foreground" key={`${split.paymentMethod}-${index}`}>
                      <span>{PAYMENT_LABELS[split.paymentMethod]}</span>
                      <span className="font-medium text-foreground">R$ {Number(split.amount).toFixed(2).replace(".", ",")}</span>
                    </div>
                  ))}
                  {change > 0 && <div className="flex justify-between border-t pt-2 font-medium text-emerald-700"><span>Troco</span><span>R$ {change.toFixed(2).replace(".", ",")}</span></div>}
                </div>
              </div>
              {checkoutNotes.trim() && <div className="rounded-lg border p-3 text-sm"><p className="mb-1 font-medium">Observação</p><p className="text-muted-foreground">{checkoutNotes}</p></div>}
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" disabled={checkout.isPending} onClick={() => setCheckoutFinalConfirmationOpen(false)}>
                  Voltar e editar
                </Button>
                <Button className="flex-1 gap-1" disabled={checkout.isPending} onClick={submitCheckout}>
                  {checkout.isPending ? <RefreshCw className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  {checkout.isPending ? "Registrando..." : "Confirmar pagamento"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!storeOrderReceipt} onOpenChange={open => !open && setStoreOrderReceipt(null)}>
        <DialogContent className="max-w-md p-5 sm:p-6">
          {storeOrderReceipt && (
            <div className="space-y-5">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-emerald-800">
                  <CheckCircle2 className="h-5 w-5" />
                  Pagamento registrado
                </DialogTitle>
              </DialogHeader>
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
                <p className="font-semibold">Pedido #{storeOrderReceipt.order.id} · R$ {storeOrderReceipt.total.toFixed(2).replace(".", ",")}</p>
                <p className="mt-1">Abra o comprovante agora. Se a janela não aparecer, use a opção de reimpressão.</p>
              </div>
              <div className="space-y-2">
                <Button className="h-11 w-full gap-2" onClick={() => {
                  const opened = printStoreOrderReceipt(storeOrderReceipt);
                  if (!opened) {
                    toast.error("O navegador bloqueou a nova janela. Libere pop-ups e tente reimprimir.");
                    return;
                  }
                  setStoreReceiptPrintAttempted(true);
                }}>
                  <Printer className="h-4 w-4" />
                  {storeReceiptPrintAttempted ? "Reimprimir comprovante" : "Abrir comprovante"}
                </Button>
                <Button variant="outline" className="w-full" onClick={() => setStoreOrderReceipt(null)}>
                  {storeReceiptPrintAttempted ? "Comprovante abriu — concluir" : "Concluir sem comprovante"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
